import React, { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { ArrowRight, Loader2, Paperclip, Mic, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ChatInputProps } from '@/types/chat';
import { useChatInterface } from '@/contexts/ChatInterfaceContext';
import { useFileUpload } from '@/hooks/useFileUpload';
import { useAuth } from '@/contexts/AuthContext';
import VoiceRecorder from '@/components/posit/VoiceRecorder';
import { FileAttachment } from '@/components/integrations/FileAttachment';
import { validateFile } from '@/utils/fileValidation';
import { useToast } from '@/hooks/use-toast';
import { FileAttachment as FileAttachmentType } from '@/types/posit';
import { useLocation, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';
import VoiceChatButton from './VoiceChatButton';
import SaveStar from './SaveStar';

const ChatInput: React.FC<ChatInputProps> = ({
  variant = 'conversation',
  placeholder = "How can I help?",
  disabled = false,
  onSubmit,
  showPositSelector = false,
  externalValue,
  onExternalChange
}) => {
  const [internalContent, setInternalContent] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);
  const [isReviewingPosit, setIsReviewingPosit] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(300);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const stopRecordingRef = useRef<(() => void) | null>(null);
  const shouldSubmitAfterProcessingRef = useRef(false);
  const pendingContentRef = useRef<string | null>(null);
  
  const { currentPosit, isLoading, welcomeMode, messages } = useChatInterface();
  const { user } = useAuth();
  const { toast } = useToast();
  const location = useLocation();
  const isMobile = useIsMobile();
  
  // Detect if we're in an update context (should not show pills)
  const isUpdateContext = location.pathname.includes('/update-posit') || location.pathname.includes('/create-posit');
  
  // Initialize file upload hook
  const {
    attachedFiles,
    addFile,
    removeFile,
    uploadFiles,
    isUploading,
    clearFiles
  } = useFileUpload({
    positId: currentPosit?.id,
    userId: user?.id
  });
  
  // Use external value if provided, otherwise use internal state
  const content = externalValue !== undefined ? externalValue : internalContent;
  const setContent = onExternalChange || setInternalContent;

  // Fetch starter questions for chat context only
  const { data: starterQuestions = [] } = useQuery({
    queryKey: ['starter-questions', currentPosit?.id, currentPosit?.updated_at],
    queryFn: async () => {
      if (!currentPosit?.id || isUpdateContext) return [];
      
      try {
        // Prefer grounded generator; fall back to legacy
        // Run both sources in parallel and return the first non-empty set
        const legacyPromise = supabase.functions.invoke('generate-starter-questions', {
          body: { 
            positId: currentPosit.id,
            userId: user?.id,
            format: 'simple',
            forceRegenerate: Math.random() < 0.2
          }
        });
        const batchPromise = supabase.functions.invoke('generate-batch-test-cases', {
          body: {
            positId: currentPosit.id,
            userId: user?.id
          }
        });

        const [legacy, batch] = await Promise.allSettled([legacyPromise, batchPromise]);

        const legacyQs =
          legacy.status === 'fulfilled' && Array.isArray(legacy.value?.data?.questions)
            ? legacy.value.data.questions.map((q: any) => String(q || '')).filter(Boolean)
            : [];

        const batchQs =
          batch.status === 'fulfilled' && Array.isArray(batch.value?.data?.cases)
            ? batch.value.data.cases.map((c: any) => String(c.text || '')).filter(Boolean)
            : [];

        let result = legacyQs.length ? legacyQs : batchQs;

        // Client-side fallback if nothing returned
        if ((!result || result.length === 0) && currentPosit?.title) {
          const t = currentPosit.title;
          result = [
            `What is ${t}?`,
            `Give me an overview of ${t}.`,
            `What are the most important details about ${t}?`,
          ];
        }

        // Cache locally for instant next load
        try {
          if (result.length > 0) {
            localStorage.setItem(`starter-questions:${currentPosit.id}`, JSON.stringify(result));
          }
        } catch (_) {}

        return result;
      } catch (error) {
        // Fallback to cached questions if available
        try {
          const cached = localStorage.getItem(`starter-questions:${currentPosit?.id}`);
          if (cached) return JSON.parse(cached);
        } catch (_) {}
        // Final fallback: derive from title if present
        if (currentPosit?.title) {
          const t = currentPosit.title;
          return [
            `What is ${t}?`,
            `Give me an overview of ${t}.`,
            `What are the most important details about ${t}?`,
          ];
        }
        return [];
      }
    },
    enabled: !!currentPosit?.id && !isUpdateContext,
    staleTime: 5 * 60 * 1000,
    refetchInterval: undefined,
    initialData: () => {
      try {
        if (Array.isArray(currentPosit?.starter_questions) && currentPosit!.starter_questions!.length > 0) {
          return currentPosit!.starter_questions!;
        }
        const cached = localStorage.getItem(`starter-questions:${currentPosit?.id}`);
        return cached ? JSON.parse(cached) : [];
      } catch {
        return [];
      }
    }
  });

  const pillContainerRef = useRef<HTMLDivElement>(null);
  const scrollPills = (dir: 'left' | 'right') => {
    const node = pillContainerRef.current;
    if (!node) return;
    const delta = dir === 'left' ? -240 : 240;
    node.scrollBy({ left: delta, behavior: 'smooth' });
  };
  const truncatePill = (q: string) => (q.length > 60 ? q.slice(0, 57) + '…' : q);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      
      // Start with minimal height
      const minHeight = content.trim() === '' ? 44 : 44; // Single line height
      const maxHeight = 200; // Max height before scrolling
      
      // Reset height to measure content
      textarea.style.height = minHeight + 'px';
      
      // Only expand if there's actual content that needs more space
      if (content.trim() !== '') {
        const scrollHeight = textarea.scrollHeight;
        if (scrollHeight > minHeight) {
          const newHeight = Math.min(scrollHeight, maxHeight);
          textarea.style.height = newHeight + 'px';
        }
      }
    }
  }, [content]);

  // Autofocus the textarea when available and enabled (on mount and when state allows)
  useEffect(() => {
    const node = textareaRef.current;
    if (!node) return;
    if (disabled || isLoading || isProcessingAudio) return;
    const timer = setTimeout(() => {
      try {
        node.focus({ preventScroll: true });
        const valueLength = node.value?.length ?? 0;
        if (valueLength > 0) {
          node.setSelectionRange(valueLength, valueLength);
        }
      } catch (_) {}
    }, 0);
    return () => clearTimeout(timer);
  }, [disabled, isLoading, isProcessingAudio]);

  // Handle pending content after voice processing
  useEffect(() => {
    if (pendingContentRef.current && content === pendingContentRef.current && shouldSubmitAfterProcessingRef.current) {
      shouldSubmitAfterProcessingRef.current = false;
      pendingContentRef.current = null;
      handleSubmit();
    }
  }, [content]);

  // Initialize textarea height on mount (do not auto-focus to avoid highlight)
  useEffect(() => {
    if (textareaRef.current && !disabled) {
      textareaRef.current.style.height = '44px';
    }
  }, [disabled]);

  const handleSubmit = useCallback(async () => {
    if (isRecording && stopRecordingRef.current) {
      // If recording, stop and submit after processing
      setIsProcessingAudio(true);
      shouldSubmitAfterProcessingRef.current = true;
      stopRecordingRef.current();
      return;
    }
    
    if (isProcessingAudio) {
      // If processing audio, wait and submit after it's done
      shouldSubmitAfterProcessingRef.current = true;
      return;
    }
    
    if ((!content.trim() && attachedFiles.length === 0) || disabled || isLoading) return;
    
    // Upload files first if any are attached and capture permanent URLs
    let attachmentsToSend = attachedFiles;
    if (attachedFiles.length > 0) {
      attachmentsToSend = await uploadFiles();
    }
    
    // Clear the input immediately so it doesn't persist while sending
    const contentToSend = content;
    setContent('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    // Call onSubmit with content and the finalized attachments (with permanent URLs)
    await onSubmit?.(contentToSend, attachmentsToSend);
    
    // Clear uploaded files from UI
    clearFiles();
    
    // Reset textarea height (do not refocus to avoid highlight)
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [content, disabled, isLoading, onSubmit, attachedFiles, uploadFiles, isRecording, isProcessingAudio]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // File attachment handlers
  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      const validation = validateFile(file);
      if (!validation.valid) {
        toast({
          title: "File not supported",
          description: validation.error,
          variant: "destructive"
        });
        return;
      }
      
      addFile(file);
    });

    // Reset input
    e.target.value = '';
  };

  const handleFileRemove = (fileId: string) => {
    removeFile(fileId);
  };

  // Voice recording handlers
  const handleVoiceTranscription = async (text: string) => {
    if (!text.trim()) {
      setIsProcessingAudio(false);
      return;
    }
    
    const newContent = content ? `${content}\n${text}` : text;
    pendingContentRef.current = newContent;
    setContent(newContent);
    setIsProcessingAudio(false);
  };

  const handleRecordingStateChange = (recording: boolean, remaining: number) => {
    setIsRecording(recording);
    setTimeRemaining(remaining);
  };

  const handleProcessingStateChange = (processing: boolean) => {
    setIsProcessingAudio(processing);
  };

  const handleGapsClick = useCallback(async () => {
    if (disabled || isLoading || isProcessingAudio || isReviewingPosit) return;
    try {
      setIsReviewingPosit(true);
      // Minimal, conversational gaps prompt to keep backend unchanged
      const prompt = 'Where are the current gaps in my Posit? Suggest 2–3 brief follow-up questions I can answer to improve it.';
      await onSubmit?.(prompt, attachedFiles);
    } finally {
      setIsReviewingPosit(false);
    }
  }, [disabled, isLoading, isProcessingAudio, isReviewingPosit, onSubmit, attachedFiles]);

    const FileAttachmentButton = () => (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 rounded-lg"
            disabled={disabled || isLoading || isProcessingAudio}
            onClick={handleFileSelect}
            aria-label="Attach files"
            title="Attach files"
          >
            {isUploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Paperclip className="h-5 w-5" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">Attach files</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );



  return (
      <div className={cn(
        "relative",
        variant === 'welcome' && "max-w-2xl mx-auto px-4",
        variant === 'conversation' && "sticky bottom-0 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t px-4 py-3"
      )}>
        {/* File attachments display */}
        {attachedFiles.length > 0 && (
          <div className="mb-2 max-w-4xl mx-auto">
            <div className="flex flex-wrap gap-2">
              {attachedFiles.map((file) => (
                <div 
                  key={file.id}
                  className="group relative rounded-lg border border-border p-2 hover:border-primary/30 transition-colors"
                >
                  <FileAttachment
                    id={file.id}
                    name={file.name}
                    mimeType={file.mimeType}
                    source={file.source}
                    compact={true}
                  />
                  <button
                    onClick={() => handleFileRemove(file.id)}
                    className="absolute -top-2 -right-2 bg-background border border-border rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Title + Smart Chat Pills (above composer). Always show title in welcome mode; pills only if available */}
        {!isUpdateContext && welcomeMode && !content.trim() && (
          <div className="mb-4 px-4 w-full opacity-0 animate-[fadeInUp_0.6s_ease-out_0.1s_forwards]">
            <div className="flex flex-col items-center gap-4 max-w-2xl mx-auto">
              {/* Posit Title (always visible in welcome mode) */}
              {currentPosit?.title && (
                <h1 className="text-white font-semibold text-xl opacity-90 text-center break-words max-w-full">
                  {currentPosit.title}
                </h1>
              )}
              {/* Smart Pills (only render when we have questions) */}
              {starterQuestions.length > 0 && (
                <div className="relative w-full">
                  <div className="absolute left-0 top-0 h-full w-8 pointer-events-none bg-gradient-to-r from-background to-transparent z-0" />
                  <div className="absolute right-0 top-0 h-full w-8 pointer-events-none bg-gradient-to-l from-background to-transparent z-0" />
                  <div className="flex items-center gap-2">
                    <button onClick={() => scrollPills('left')} aria-label="Scroll left" className="hidden sm:inline-flex h-8 w-8 items-center justify-center text-white/60 hover:text-white relative z-10 leading-none -translate-y-[4px]">
                      ‹
                    </button>
                    <div
                      ref={pillContainerRef}
                      className="flex gap-2 overflow-x-auto scrollbar-none snap-x snap-mandatory w-full px-1"
                    >
                      {starterQuestions.slice(0, 10).map((question: string, index: number) => (
                        <button
                          key={index}
                          onClick={() => {
                            setContent(question);
                            setTimeout(() => { onSubmit?.(question, attachedFiles); }, 50);
                          }}
                          style={{ animationDelay: `${index * 120}ms` }}
                          className={cn(
                            "px-4 py-2 text-sm border border-white/10 bg-white/5 text-gray-300",
                            "rounded-xl min-h-[40px] max-h-[56px] items-center cursor-pointer transition-all duration-200 backdrop-blur-sm",
                            "hover:border-white/20 hover:bg-white/10 hover:text-white",
                            "active:scale-95 shrink-0 snap-start",
                            "opacity-0 animate-[fadeInUp_0.4s_ease-out_forwards]"
                            )}
                            title={question}
                          >
                            <span className="block text-center whitespace-normal break-words leading-snug line-clamp-2 max-w-[240px] sm:max-w-[320px]">
                              {question}
                            </span>
                          </button>
                        ))}
                    </div>
                    <button onClick={() => scrollPills('right')} aria-label="Scroll right" className="hidden sm:inline-flex h-8 w-8 items-center justify-center text-white/60 hover:text-white relative z-10 leading-none -translate-y-[4px]">
                      ›
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Input Container */}
        <div className={cn(
          "relative bg-card border border-border/60 rounded-2xl transition-[colors,border-color] duration-\[var(--duration-normal)\] ease-\[var(--ease-standard)\] max-w-4xl mx-auto",
          isExpanded && "border-border/80"
        )}>
          
          {/* Textarea - Clean and free of icon overlap */}
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsExpanded(true)}
            onBlur={() => setIsExpanded(false)}
            placeholder={placeholder}
            disabled={disabled || isLoading || isProcessingAudio}
            rows={1}
            className={cn(
              "w-full resize-none bg-transparent border-none outline-none px-3 py-3 text-sm",
              "placeholder:text-muted-foreground",
              "max-h-[200px]",
              isExpanded
                ? "overflow-y-auto scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent"
                : "overflow-hidden"
            )}
            style={{ overflowY: isExpanded ? 'auto' : 'hidden' }}
          />
          
          {/* Action Buttons Row - Below the input */}
          <div className="flex items-center justify-between px-3 pt-1 pb-2 border-t border-border/50">
            <div className="flex items-center gap-1">
              <FileAttachmentButton />
              {/* Voice chat temporarily hidden - focusing on dictation optimization */}
              {false && currentPosit?.id && (
                <VoiceChatButton 
                  positId={currentPosit.id}
                  disabled={disabled || isLoading}
                  className="h-8 w-8"
                />
              )}
              <VoiceRecorder
                onTranscriptionComplete={handleVoiceTranscription}
                onRecordingStateChange={handleRecordingStateChange}
                onProcessingStateChange={handleProcessingStateChange}
                stopRecording={(stopFn) => {
                  stopRecordingRef.current = stopFn;
                }}
                userId={user?.id}
                isProcessing={isProcessingAudio}
                className=""
              />
              {/* Save Star to the right of dictation button, only in regular chat */}
              {currentPosit?.id && !isUpdateContext && (
                <SaveStar positId={currentPosit.id} disabled={disabled || isLoading} />
              )}
              {/* Minimal Gaps pill for update/create contexts */}
              {isUpdateContext && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 px-3 rounded-full"
                        disabled={disabled || isLoading || isProcessingAudio || isReviewingPosit}
                        onClick={handleGapsClick}
                      >
                        Knowledge Gaps
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">Review Posit and suggest quick fixes</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            {/* Right actions: Send */}
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={(!content.trim() && attachedFiles.length === 0) || disabled || isLoading || isProcessingAudio}
                className="rounded-lg h-8 w-8 p-0"
              >
                {isLoading || isProcessingAudio ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>
        </div>
        
        {/* Terms and Privacy notice for unauthenticated users before first user message */}
        {!user && (!messages || !messages.some(m => m.role === 'user')) && (
          <div className="flex items-center justify-center mt-2 px-2 max-w-4xl mx-auto">
            <p className="text-[11px] leading-snug text-muted-foreground text-center">
              By continuing, you agree to our{' '}
              <Link to="/terms" className="underline hover:text-foreground">Terms of Service</Link>{' '}and{' '}
              <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
            </p>
          </div>
        )}

        {/* Recording indicator */}
        {isRecording && (
          <div className="flex items-center justify-center mt-2 text-xs text-red-500 animate-pulse max-w-4xl mx-auto">
            Recording... {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
          </div>
        )}

        {/* Processing indicator */}
        {isProcessingAudio && (
          <div className="flex items-center justify-center mt-2 text-xs text-muted-foreground max-w-4xl mx-auto">
            <span className="mr-2">Transcribing</span>
            <div className="flex space-x-1">
              <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        )}
        {isReviewingPosit && !isProcessingAudio && (
          <div className="flex items-center justify-center mt-2 text-xs text-muted-foreground max-w-4xl mx-auto">
            <span className="mr-2">Reviewing Posit</span>
            <div className="flex space-x-1">
              <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
              <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        )}
        
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          accept="image/*,.pdf,.doc,.docx,.txt,.md"
          multiple
        />
        
        

        {/* Input hints - Hidden on mobile */}
        {variant === 'conversation' && !isMobile && (
          <div className="flex items-center justify-center mt-2 px-2">
            <p className="text-xs text-muted-foreground">
              Press Enter to send, Shift+Enter for new line
            </p>
          </div>
        )}
      </div>
  );
};

export default ChatInput; 